import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import { 
  FaCity, 
  FaChartBar, 
  FaQuestionCircle, 
  FaShare, 
  FaExclamationTriangle,
  FaBuilding,
  FaRulerCombined,
  FaMoneyBillWave,
  FaHome,
  FaHardHat,
  FaTruckLoading,
  FaCalculator,
  FaMapMarkerAlt,
  FaHome as FaHouse,
  FaVectorSquare,
  FaRuler,
  FaLandmark,
  FaChartLine,
  FaBalanceScale,
  FaTools,
  FaBolt,
  FaTint,
  FaCubes,
  FaPaintRoller
} from 'react-icons/fa';
import { 
  GiBrickWall, 
  GiHammerNails,
  GiToolbox
} from 'react-icons/gi';
import { 
  MdApartment,
  MdConstruction,
  MdLocationCity,
  MdArchitecture,
  MdEngineering
} from 'react-icons/md';
import constructionAnimation from '../../assets/animations/login.json';
import '../../styles/websitepagescss/ConstructionCost.css';

const ConstructionCost = () => {
  const [formData, setFormData] = useState({
    areaSize: '',
    areaUnit: 'marla',
    constructionType: 'complete',
    constructionMode: 'withMaterial',
    city: 'lahore'
  });

  const [totalCost, setTotalCost] = useState(0);
  const [greyStructureCost, setGreyStructureCost] = useState(0);
  const [finishingCost, setFinishingCost] = useState(0);
  const [labourCost, setLabourCost] = useState(0);
  const [pricePerSqFt, setPricePerSqFt] = useState(0);
  const [breakdown, setBreakdown] = useState({});
  const [detailedBreakdown, setDetailedBreakdown] = useState({});
  const [selectedPopular, setSelectedPopular] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // City-wise cost multipliers
  const cityMultipliers = {
    lahore: 1.00,
    karachi: 1.15,
    islamabad: 1.25,
    rawalpindi: 0.95,
    faisalabad: 0.90,
    multan: 0.85,
    gujranwala: 0.88,
    peshawar: 0.92
  };

  const areaConversions = {
    marla: 225,
    kanal: 20,
    sqft: 1,
    sqyd: 9,
    acre: 43560
  };

  const baseLahoreCosts = {
    '3-marla': { total: 6390000, areaSqFt: 1215 },
    '4-marla': { total: 7930000, areaSqFt: 1620 },
    '5-marla': { total: 9230000, areaSqFt: 2025 },
    '6-marla': { total: 10400000, areaSqFt: 2295 },
    '7-marla': { total: 11600000, areaSqFt: 2678 },
    '8-marla': { total: 13500000, areaSqFt: 3060 },
    '9-marla': { total: 14200000, areaSqFt: 3375 },
    '10-marla': { total: 14800000, areaSqFt: 3375 },
    '11-marla': { total: 16200000, areaSqFt: 3713 },
    '1-kanal': { total: 27300000, areaSqFt: 6300 },
    '2-kanal': { total: 52000000, areaSqFt: 12600 },
    '3-kanal': { total: 76600000, areaSqFt: 18900 },
    '4-kanal': { total: 101300000, areaSqFt: 25200 },
    '5-kanal': { total: 126000000, areaSqFt: 31500 },
    '6-kanal': { total: 150600000, areaSqFt: 37800 },
    '7-kanal': { total: 175500000, areaSqFt: 44100 },
    '8-kanal': { total: 200200000, areaSqFt: 50400 },
    '9-kanal': { total: 224900000, areaSqFt: 56700 },
    '10-kanal': { total: 249500000, areaSqFt: 63000 },
    '20-kanal': { total: 496400000, areaSqFt: 126000 }
  };

  const costDistribution = {
    '3-marla': { grey: 0.467, finishing: 0.452, labour: 0.081 },
    '4-marla': { grey: 0.497, finishing: 0.415, labour: 0.087 },
    '5-marla': { grey: 0.506, finishing: 0.400, labour: 0.094 },
    '6-marla': { grey: 0.518, finishing: 0.387, labour: 0.094 },
    '7-marla': { grey: 0.524, finishing: 0.381, labour: 0.099 },
    '8-marla': { grey: 0.532, finishing: 0.373, labour: 0.097 },
    '9-marla': { grey: 0.529, finishing: 0.376, labour: 0.098 },
    '10-marla': { grey: 0.525, finishing: 0.379, labour: 0.098 },
    '11-marla': { grey: 0.533, finishing: 0.369, labour: 0.098 },
    '1-kanal': { grey: 0.557, finishing: 0.346, labour: 0.099 },
    '2-kanal': { grey: 0.577, finishing: 0.319, labour: 0.104 },
    '3-kanal': { grey: 0.585, finishing: 0.311, labour: 0.106 },
    '4-kanal': { grey: 0.588, finishing: 0.306, labour: 0.106 },
    '5-kanal': { grey: 0.590, finishing: 0.302, labour: 0.108 },
    '6-kanal': { grey: 0.592, finishing: 0.301, labour: 0.107 },
    '7-kanal': { grey: 0.593, finishing: 0.299, labour: 0.108 },
    '8-kanal': { grey: 0.594, finishing: 0.298, labour: 0.108 },
    '9-kanal': { grey: 0.595, finishing: 0.297, labour: 0.108 },
    '10-kanal': { grey: 0.595, finishing: 0.296, labour: 0.109 },
    '20-kanal': { grey: 0.598, finishing: 0.293, labour: 0.109 }
  };

  const categoryBreakdown = {
    '3-marla': { plumbing: 0.079, electrical: 0.118, woodMetalTiles: 0.278, fittings: 0.074, foundation: 0.451 },
    '4-marla': { plumbing: 0.073, electrical: 0.105, woodMetalTiles: 0.267, fittings: 0.060, foundation: 0.495 },
    '5-marla': { plumbing: 0.071, electrical: 0.099, woodMetalTiles: 0.267, fittings: 0.052, foundation: 0.512 },
    '6-marla': { plumbing: 0.067, electrical: 0.094, woodMetalTiles: 0.264, fittings: 0.046, foundation: 0.529 },
    '7-marla': { plumbing: 0.066, electrical: 0.091, woodMetalTiles: 0.266, fittings: 0.041, foundation: 0.539 },
    '8-marla': { plumbing: 0.062, electrical: 0.087, woodMetalTiles: 0.261, fittings: 0.043, foundation: 0.550 },
    '9-marla': { plumbing: 0.061, electrical: 0.086, woodMetalTiles: 0.266, fittings: 0.041, foundation: 0.549 },
    '10-marla': { plumbing: 0.060, electrical: 0.086, woodMetalTiles: 0.270, fittings: 0.039, foundation: 0.546 },
    '11-marla': { plumbing: 0.059, electrical: 0.083, woodMetalTiles: 0.266, fittings: 0.036, foundation: 0.556 },
    '1-kanal': { plumbing: 0.051, electrical: 0.074, woodMetalTiles: 0.259, fittings: 0.029, foundation: 0.586 },
    '2-kanal': { plumbing: 0.048, electrical: 0.066, woodMetalTiles: 0.254, fittings: 0.016, foundation: 0.615 },
    '3-kanal': { plumbing: 0.047, electrical: 0.063, woodMetalTiles: 0.252, fittings: 0.011, foundation: 0.627 },
    '4-kanal': { plumbing: 0.046, electrical: 0.061, woodMetalTiles: 0.250, fittings: 0.009, foundation: 0.632 },
    '5-kanal': { plumbing: 0.046, electrical: 0.061, woodMetalTiles: 0.250, fittings: 0.008, foundation: 0.635 },
    '6-kanal': { plumbing: 0.046, electrical: 0.060, woodMetalTiles: 0.250, fittings: 0.007, foundation: 0.638 },
    '7-kanal': { plumbing: 0.046, electrical: 0.060, woodMetalTiles: 0.249, fittings: 0.006, foundation: 0.640 },
    '8-kanal': { plumbing: 0.046, electrical: 0.059, woodMetalTiles: 0.249, fittings: 0.005, foundation: 0.641 },
    '9-kanal': { plumbing: 0.046, electrical: 0.059, woodMetalTiles: 0.249, fittings: 0.005, foundation: 0.642 },
    '10-kanal': { plumbing: 0.046, electrical: 0.059, woodMetalTiles: 0.248, fittings: 0.005, foundation: 0.643 },
    '20-kanal': { plumbing: 0.045, electrical: 0.058, woodMetalTiles: 0.248, fittings: 0.003, foundation: 0.646 }
  };

  const getFixedCosts = (bucketKey, city) => {
    const baseCosts = {
      kitchen: 55000,
      switches: 224000,
      windows: bucketKey.includes('kanal') || parseInt(bucketKey) >= 8 ? 253000 : 173000,
      wardrobes: bucketKey.includes('kanal') || parseInt(bucketKey) >= 8 ? 594000 : 441000,
      bathroom: bucketKey.includes('kanal') || parseInt(bucketKey) >= 8 ? 661000 : 378000
    };

    const multiplier = cityMultipliers[city];
    const adjustedCosts = {};
    Object.keys(baseCosts).forEach(key => {
      adjustedCosts[key] = Math.round(baseCosts[key] * multiplier);
    });

    return adjustedCosts;
  };

  const getBucketKey = (areaSize, areaUnit) => {
    let areaSqFt;
    
    if (areaUnit === 'marla') {
      const marlaSize = parseInt(areaSize);
      if (marlaSize <= 3) return '3-marla';
      if (marlaSize <= 4) return '4-marla';
      if (marlaSize <= 5) return '5-marla';
      if (marlaSize <= 6) return '6-marla';
      if (marlaSize <= 7) return '7-marla';
      if (marlaSize <= 8) return '8-marla';
      if (marlaSize <= 9) return '9-marla';
      if (marlaSize <= 10) return '10-marla';
      if (marlaSize <= 11) return '11-marla';
      const kanalSize = Math.ceil(marlaSize / 20);
      return `${kanalSize}-kanal`;
    } 
    else if (areaUnit === 'kanal') {
      const kanalSize = parseInt(areaSize);
      if (kanalSize <= 10) return `${kanalSize}-kanal`;
      if (kanalSize <= 20) return '20-kanal';
      return '20-kanal';
    }
    else if (areaUnit === 'acre') {
      const kanalSize = Math.ceil(parseInt(areaSize) * 8);
      if (kanalSize <= 10) return `${kanalSize}-kanal`;
      return '20-kanal';
    }
    else {
      areaSqFt = parseInt(areaSize) * (areaUnit === 'sqyd' ? 9 : 1);
      const buckets = Object.keys(baseLahoreCosts);
      
      let closestKey = '5-marla';
      let minDiff = Math.abs(areaSqFt - baseLahoreCosts['5-marla'].areaSqFt);

      buckets.forEach(key => {
        const diff = Math.abs(areaSqFt - baseLahoreCosts[key].areaSqFt);
        if (diff < minDiff) {
          minDiff = diff;
          closestKey = key;
        }
      });

      return closestKey;
    }
  };

  const getScalingFactor = (bucketKey, areaSize, areaUnit) => {
    const baseData = baseLahoreCosts[bucketKey];
    if (!baseData) return 1;

    let targetArea;
    
    if (areaUnit === 'marla') {
      targetArea = parseInt(areaSize) * areaConversions.marla;
    } else if (areaUnit === 'kanal') {
      targetArea = parseInt(areaSize) * areaConversions.marla * 20;
    } else if (areaUnit === 'sqft') {
      targetArea = parseInt(areaSize);
    } else if (areaUnit === 'sqyd') {
      targetArea = parseInt(areaSize) * 9;
    } else if (areaUnit === 'acre') {
      targetArea = parseInt(areaSize) * 43560;
    }

    if (Math.abs(targetArea - baseData.areaSqFt) < 100) {
      return 1;
    }

    const baseArea = baseData.areaSqFt;
    const areaRatio = targetArea / baseArea;
    
    if (areaRatio <= 1.2) return 1;
    if (areaRatio <= 2) return 1 + (areaRatio - 1) * 0.9;
    if (areaRatio <= 5) return 1 + (areaRatio - 1) * 0.85;
    return 1 + (areaRatio - 1) * 0.8;
  };

  const calculateCost = () => {
    if (!formData.areaSize || formData.areaSize <= 0) {
      resetCosts();
      return;
    }

    const bucketKey = getBucketKey(formData.areaSize, formData.areaUnit);
    const baseData = baseLahoreCosts[bucketKey];
    
    if (!baseData) {
      resetCosts();
      return;
    }

    const scalingFactor = getScalingFactor(bucketKey, formData.areaSize, formData.areaUnit);
    const cityMultiplier = cityMultipliers[formData.city];
    
    const total = Math.round(baseData.total * scalingFactor * cityMultiplier);

    const distribution = costDistribution[bucketKey];
    const greyStructure = Math.round(total * distribution.grey);
    const finishing = formData.constructionType === 'complete' ? Math.round(total * distribution.finishing) : 0;
    const labour = Math.round(total * distribution.labour);
    
    const areaSqFt = baseData.areaSqFt * scalingFactor;
    const pricePerSqFt = Math.round(total / areaSqFt);

    const categories = categoryBreakdown[bucketKey];
    const calculatedBreakdown = {};
    Object.keys(categories).forEach(category => {
      calculatedBreakdown[category] = Math.round(total * categories[category]);
    });

    const fixedCosts = getFixedCosts(bucketKey, formData.city);
    const calculatedDetailed = calculateDetailedBreakdown(calculatedBreakdown, bucketKey, fixedCosts);

    setTotalCost(total);
    setGreyStructureCost(greyStructure);
    setFinishingCost(finishing);
    setLabourCost(labour);
    setPricePerSqFt(pricePerSqFt);
    setBreakdown(calculatedBreakdown);
    setDetailedBreakdown(calculatedDetailed);
  };

  const calculateDetailedBreakdown = (breakdown, bucketKey, fixedCosts) => {
    const isLargeHouse = bucketKey.includes('kanal') || parseInt(bucketKey) >= 8;

    return {
      plumbing: calculatePlumbingBreakdown(breakdown.plumbing, isLargeHouse),
      electrical: calculateElectricalBreakdown(breakdown.electrical, isLargeHouse, fixedCosts),
      woodMetalTiles: calculateWoodMetalTilesBreakdown(breakdown.woodMetalTiles, isLargeHouse, fixedCosts),
      fittings: calculateFittingsBreakdown(breakdown.fittings, isLargeHouse, fixedCosts),
      foundation: calculateFoundationBreakdown(breakdown.foundation, isLargeHouse)
    };
  };

  const calculatePlumbingBreakdown = (totalPlumbing, isLargeHouse) => {
    const percentages = isLargeHouse ? {
      acDrainage: 0.0327, waterSupply: 0.1118, drainagePiping: 0.1523,
      gasPiping: 0.1432, insulation: 0.0735, pumps: 0.1152,
      upvc: 0.0987, labour: 0.2726
    } : {
      acDrainage: 0.0175, waterSupply: 0.0598, drainagePiping: 0.0818,
      gasPiping: 0.0758, insulation: 0.0388, pumps: 0.5937,
      upvc: 0.0414, labour: 0.0912
    };

    const items = {};
    Object.keys(percentages).forEach(item => {
      items[item] = Math.round(totalPlumbing * percentages[item]);
    });
    return items;
  };

  const calculateElectricalBreakdown = (totalElectrical, isLargeHouse, fixedCosts) => {
    const percentages = isLargeHouse ? {
      greyStructure: 0.1802, finishing: 0.8198, conduits: 0.0694,
      lights: 0.0554, switches: 0.0360, wires: 0.6590, labour: 0.1802
    } : {
      greyStructure: 0.1302, finishing: 0.8698, conduits: 0.0418,
      lights: 0.3046, switches: 0.2477, wires: 0.3416, labour: 0.0643
    };

    const items = {};
    Object.keys(percentages).forEach(item => {
      items[item] = Math.round(totalElectrical * percentages[item]);
    });
    
    items.switches = fixedCosts.switches;
    
    return items;
  };

  const calculateWoodMetalTilesBreakdown = (totalWoodMetalTiles, isLargeHouse, fixedCosts) => {
    const percentages = isLargeHouse ? {
      metalWorks: 0.0600, paint: 0.2463, tiles: 0.5750
    } : {
      metalWorks: 0.1054, paint: 0.1893, tiles: 0.3600
    };

    const items = {};
    Object.keys(percentages).forEach(item => {
      items[item] = Math.round(totalWoodMetalTiles * percentages[item]);
    });
    
    items.wardrobes = fixedCosts.wardrobes;
    items.windows = fixedCosts.windows;
    
    return items;
  };

  const calculateFittingsBreakdown = (totalFittings, isLargeHouse, fixedCosts) => {
    const items = {
      bathroom: fixedCosts.bathroom,
      kitchen: fixedCosts.kitchen
    };
    
    const remaining = totalFittings - fixedCosts.bathroom - fixedCosts.kitchen;
    items.geyser = Math.max(0, remaining);
    
    return items;
  };

  const calculateFoundationBreakdown = (totalFoundation, isLargeHouse) => {
    const percentages = isLargeHouse ? {
      bricksCement: 0.6925, excavationSteel: 0.1594,
      insulation: 0.0117, labour: 0.1364
    } : {
      bricksCement: 0.6635, excavationSteel: 0.1765,
      insulation: 0.0125, labour: 0.1475
    };

    const items = {};
    Object.keys(percentages).forEach(item => {
      items[item] = Math.round(totalFoundation * percentages[item]);
    });
    return items;
  };

  const resetCosts = () => {
    setTotalCost(0);
    setGreyStructureCost(0);
    setFinishingCost(0);
    setLabourCost(0);
    setPricePerSqFt(0);
    setBreakdown({});
    setDetailedBreakdown({});
  };

  useEffect(() => {
    if (formData.areaSize && formData.areaSize > 0) {
      calculateCost();
    } else {
      resetCosts();
    }
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    
    if (num >= 10000000) {
      return (num / 10000000).toFixed(2) + ' Crore';
    } else if (num >= 100000) {
      return (num / 100000).toFixed(2) + ' Lakh';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + ' Thousand';
    }
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatExactNumber = (num) => {
    if (!num) return '0';
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getCategoryName = (category) => {
    const names = {
      plumbing: 'Plumbing Works',
      electrical: 'Electrical Works',
      woodMetalTiles: 'Wood, Metal and Tile Works',
      fittings: 'Fittings & Fixtures',
      foundation: 'Foundation & Structure'
    };
    return names[category] || category;
  };

  const getItemName = (item) => {
    const names = {
      acDrainage: 'AC Drainage System',
      waterSupply: 'Cold and Hot Water Supply',
      drainagePiping: 'Drainage System Piping',
      gasPiping: 'Gas Piping',
      insulation: 'Insulation Armaflex',
      pumps: 'Pumps and Drains',
      upvc: 'UPVC Pipeline for Sewerage System',
      labour: 'Labor Cost',
      greyStructure: 'Grey Structure',
      finishing: 'Finishing',
      conduits: 'Conduits & Accessories',
      lights: 'Lights, Fans and Chandeliers',
      switches: 'Switches and Back Boxes',
      wires: 'Wires & Cables',
      metalWorks: 'Metal Works',
      paint: 'Paint and Ceiling Works',
      tiles: 'Tile Work',
      wardrobes: 'Wardrobes & Cabinets',
      windows: 'Windows',
      bathroom: 'Bathroom',
      geyser: 'Geyser & Manhole',
      kitchen: 'Kitchen',
      bricksCement: 'Bricks, Cement, Sand, & Crush',
      excavationSteel: 'Excavation & Steel Reinforcement',
      roofInsulation: 'Roof Insulation, Termite & Water Proofing'
    };
    return names[item] || item;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      plumbing: 'Plumbing works include underground piping and drainage systems materials.',
      electrical: 'Electrical works include underground wiring and all electrical items.',
      woodMetalTiles: 'Wood, Metal & Tile Works include cabinets, wardrobes, flooring, and all aluminum and iron works.',
      fittings: 'Fittings & Fixtures include kitchen and bath accessories.',
      foundation: 'Foundation & Structure includes materials for the basic house structure including foundation, floors, walls, roofing, and plastering.'
    };
    return descriptions[category] || '';
  };

  const getAreaText = () => {
    const area = parseInt(formData.areaSize);
    const unit = formData.areaUnit;
    const type = formData.constructionType === 'grey' ? 'Grey Structure' : 'House';
    const city = formData.city.charAt(0).toUpperCase() + formData.city.slice(1);
    
    const unitDisplay = unit === 'sqft' ? 'Square Feet' : 
                       unit === 'sqyd' ? 'Square Yards' :
                       unit.charAt(0).toUpperCase() + unit.slice(1);
    
    return `${area} ${unitDisplay} ${type} Construction Cost in ${city}`;
  };

  const getBreakdownPercentage = (categoryCost) => {
    if (!totalCost || totalCost === 0) return '0.00';
    return ((categoryCost / totalCost) * 100).toFixed(2);
  };

  const getCityMultiplierInfo = () => {
    const multipliers = {
      lahore: 'Base rates',
      karachi: '15% higher than Lahore',
      islamabad: '25% higher than Lahore',
      rawalpindi: '5% lower than Lahore',
      faisalabad: '10% lower than Lahore',
      multan: '15% lower than Lahore',
      gujranwala: '12% lower than Lahore',
      peshawar: '8% lower than Lahore'
    };
    return multipliers[formData.city];
  };

  const popularCalculations = [
    { size: '5', unit: 'marla', type: 'Double Story', area: '2,025 Sq. ft.' },
    { size: '10', unit: 'marla', type: 'Double Story', area: '3,375 Sq. ft.' },
    { size: '1', unit: 'kanal', type: 'Double Story', area: '6,300 Sq. ft.' },
    { size: '2', unit: 'kanal', type: 'Double Story', area: '12,600 Sq. ft.' },
    { size: '5', unit: 'kanal', type: 'Double Story', area: '31,500 Sq. ft.' },
    { size: '1', unit: 'acre', type: 'Double Story', area: '63,000 Sq. ft.' }
  ];

  const faqData = [
    {
      question: `What is the cost of building ${formData.areaSize || '___'} ${formData.areaUnit} house in ${formData.city}?`,
      answer: `The cost varies based on size, location, and construction type. Use our calculator above to get an accurate estimate for your specific requirements.`
    },
    {
      question: `Why are construction costs different in ${formData.city} compared to other cities?`,
      answer: `Construction costs vary by city due to differences in material availability, labor rates, transportation costs, and local market conditions.`
    },
    {
      question: `What is the construction cost per sq. ft in ${formData.city}?`,
      answer: `The cost per square foot depends on the construction type and quality of materials. Our calculator provides accurate per sq. ft rates based on current market data.`
    },
    {
      question: `What is the cost of building ${formData.areaSize || '___'} ${formData.areaUnit} house grey structure in ${formData.city}?`,
      answer: `Grey structure cost includes foundation, structure, walls, and roofing without finishing. Use the calculator and select 'Grey Structure' option for detailed breakdown.`
    },
    {
      question: "What is included in the grey structure?",
      answer: "Grey structure includes excavation, foundation, concrete work, brickwork, roofing, and basic plumbing/electrical conduits - essentially the basic shell of the building."
    },
    {
      question: `What is the finishing cost of building ${formData.areaSize || '___'} ${formData.areaUnit} house in ${formData.city}?`,
      answer: `Finishing costs include tiles, paint, electrical fixtures, plumbing fixtures, windows, doors, and all interior works. Select 'Complete Construction' in the calculator for detailed finishing costs.`
    }
  ];

  const handlePopularSelect = (item) => {
    setFormData(prev => ({
      ...prev,
      areaSize: item.size,
      areaUnit: item.unit
    }));
    setSelectedPopular(item);
  };

  const closePopup = () => {
    setSelectedPopular(null);
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Default animation options for Lottie
  const defaultLottieOptions = {
    loop: true,
    autoplay: true,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  return (
    <motion.div 
      className="construction-cost-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header Section */}
        <motion.div 
          className="calculator-header"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <h1>Construction Cost Calculator Pakistan 2025</h1>
          <p>Calculate accurate construction costs across major Pakistani cities with detailed breakdowns</p>
        </motion.div>

        <div className="calculator-content">
          {/* Calculator Form */}
          <motion.div 
            className="calculator-form-section"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="form-card-zameen">
              <div className="form-header">
                <h2>Construction Calculator</h2>
                <div className="form-icon">
                  <MdConstruction size={24} />
                </div>
              </div>
              
              {/* Area Size */}
              <div className="form-section">
                <label className="form-label">
                  <strong>Area Size</strong>
                  <span className="required">*</span>
                </label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    name="areaSize"
                    value={formData.areaSize}
                    onChange={handleInputChange}
                    placeholder="Enter area size"
                    min="1"
                    className="form-input"
                  />
                  <select
                    name="areaUnit"
                    value={formData.areaUnit}
                    onChange={handleInputChange}
                    className="form-select unit-select"
                  >
                    <option value="marla">Marla</option>
                    <option value="kanal">Kanal</option>
                    <option value="sqft">Square Feet</option>
                    <option value="sqyd">Square Yards</option>
                    <option value="acre">Acre</option>
                  </select>
                </div>
              </div>

              {/* Construction Type */}
              <div className="form-section">
                <label className="form-label">
                  <strong>Construction Type</strong>
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="constructionType"
                      value="grey"
                      checked={formData.constructionType === 'grey'}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    Grey Structure
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="constructionType"
                      value="complete"
                      checked={formData.constructionType === 'complete'}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    Complete House
                  </label>
                </div>
              </div>

              {/* Construction Mode */}
              <div className="form-section">
                <label className="form-label">
                  <strong>Construction Mode</strong>
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="constructionMode"
                      value="withMaterial"
                      checked={formData.constructionMode === 'withMaterial'}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    With Material
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="constructionMode"
                      value="withoutMaterial"
                      checked={formData.constructionMode === 'withoutMaterial'}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    Without Material
                  </label>
                </div>
              </div>

              {/* City Selection */}
              <div className="form-section">
                <label className="form-label">
                  <strong>Select City</strong>
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="lahore">Lahore (Base Rates)</option>
                  <option value="karachi">Karachi (+15%)</option>
                  <option value="islamabad">Islamabad (+25%)</option>
                  <option value="rawalpindi">Rawalpindi (-5%)</option>
                  <option value="faisalabad">Faisalabad (-10%)</option>
                  <option value="multan">Multan (-15%)</option>
                  <option value="gujranwala">Gujranwala (-12%)</option>
                  <option value="peshawar">Peshawar (-8%)</option>
                </select>
                <div className="city-note">
                  <small>{getCityMultiplierInfo()}</small>
                </div>
              </div>

              <div className="update-note">
                <p><FaExclamationTriangle className="icon" /> <strong>Pro Tip:</strong> Start with area size to see instant results</p>
                <p>Prices updated on 21st November, 2025</p>
              </div>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div 
            className="calculator-results-section"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {!formData.areaSize ? (
              <motion.div 
                className="welcome-card"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="welcome-animation">
                  <Lottie
                    animationData={constructionAnimation}
                    style={{ width: 200, height: 200 }}
                    {...defaultLottieOptions}
                  />
                </div>
                <h3>Welcome to Meezan Construction Calculator</h3>
                <p>Enter your construction details to get an accurate cost estimate</p>
                <div className="welcome-features">
                  {/* <div className="feature">
                    <FaCity className="feature-icon" />
                    <span>8 Major Cities</span>
                  </div>
                  <div className="feature">
                    <FaRulerCombined className="feature-icon" />
                    <span>Multiple Units</span>
                  </div>
                  <div className="feature">
                    <FaMoneyBillWave className="feature-icon" />
                    <span>Detailed Breakdown</span>
                  </div> */}
                </div>
              </motion.div>
            ) : totalCost > 0 ? (
              <>
                {/* Main Cost Card */}
                <motion.div 
                  className="main-cost-card"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <div className="cost-header">
                    <h3>{getAreaText()}</h3>
                    <div className="share-btn">
                      <FaShare className="icon" />
                      Share
                    </div>
                  </div>
                  
                  <div className="cost-badge">
                    <span>City Adjustment: {getCityMultiplierInfo()}</span>
                  </div>

                  <div className="total-cost-section">
                    <div className="total-cost-main">
                      <div className="cost-label">
                        <span>Total Estimated Cost</span>
                        <small>Inclusive of all materials and labor</small>
                      </div>
                      <strong>{formatNumber(totalCost)}</strong>
                    </div>
                    
                    <div className="cost-breakdown-grid">
                      <div className="cost-item">
                        <span>Grey Structure Material Cost</span>
                        <strong>{formatNumber(greyStructureCost)}</strong>
                      </div>
                      {formData.constructionType === 'complete' && (
                        <>
                          <div className="cost-item">
                            <span>Finishing Material Cost</span>
                            <strong>{formatNumber(finishingCost)}</strong>
                          </div>
                          <div className="cost-item">
                            <span>Labour Cost</span>
                            <strong>{formatNumber(labourCost)}</strong>
                          </div>
                        </>
                      )}
                      <div className="cost-item highlight">
                        <span>Price Per Sq Ft</span>
                        <strong>{formatExactNumber(pricePerSqFt)} PKR</strong>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Detailed Breakdown */}
                {formData.constructionType === 'complete' && (
                  <>
                    <motion.div 
                      className="detailed-breakdown-section"
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                    >
                      <div className="section-headers">
                        <h3>Cost Breakdown by Category</h3>
                        <div className="construction-type-badge">Complete Construction</div>
                      </div>
                      
                      <div className="breakdown-categories">
                        {Object.keys(breakdown).map((category, index) => (
                          <div key={category} className="breakdown-category">
                            <div className="category-header">
                              <span>{getCategoryName(category)}</span>
                              <strong>{formatNumber(breakdown[category])}</strong>
                            </div>
                            <div className="category-bar">
                              <div 
                                className="category-fill"
                                style={{ 
                                  width: `${getBreakdownPercentage(breakdown[category])}%`
                                }}
                              ></div>
                            </div>
                            <div className="category-percentage">
                              {getBreakdownPercentage(breakdown[category])}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Sub-Breakdowns */}
                    <motion.div 
                      className="sub-breakdown-section"
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 1 }}
                    >
                      <h3>Detailed Cost Analysis</h3>
                      {Object.keys(detailedBreakdown).map(category => (
                        <div key={category} className="sub-breakdown-category">
                          <div className="category-title">
                            <h4>{getCategoryName(category)}</h4>
                            <div className="category-total">
                              {formatNumber(breakdown[category])}
                            </div>
                          </div>
                          <p className="category-description">
                            {getCategoryDescription(category)}
                          </p>
                          
                          <div className="sub-items">
                            {Object.keys(detailedBreakdown[category]).map(item => (
                              <div key={item} className="sub-item">
                                <span>{getItemName(item)}</span>
                                <strong>{formatNumber(detailedBreakdown[category][item])}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  </>
                )}

                {/* Disclaimer */}
                <motion.div 
                  className="disclaimer-section"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <h4><FaExclamationTriangle className="icon" /> Important Notice</h4>
                  <p>
                    This cost estimate is for {formData.areaSize} {formData.areaUnit} double storey construction in {formData.city.charAt(0).toUpperCase() + formData.city.slice(1)}. 
                    All information is provided for general guidance only. Product prices are subject to market fluctuations and may not be 100% accurate. 
                    City-specific adjustments are based on current market trends and regional variations.
                  </p>
                </motion.div>
              </>
            ) : null}
          </motion.div>
        </div>

        {/* Information Section */}
        <motion.div 
          className="info-section-zameen"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          <div className="info-content">
            <div className="section-intro">
              <h2>Smart Construction Planning</h2>
              <p>Accurate cost estimates for your dream project across Pakistan</p>
            </div>

            {/* City Price Comparison */}
            <div className="city-comparison-section">
              <div className="section-headers">
                <h3><FaCity className="section-icon" /> City Price Comparison</h3>
                <p>Construction cost variations across major Pakistani cities</p>
              </div>
              <div className="comparison-grid">
                {Object.entries(cityMultipliers).map(([city, multiplier]) => (
                  <div key={city} className={`city-card ${formData.city === city ? 'active' : ''}`}>
                    <div className="city-header">
                      <span className="city-name">{city.charAt(0).toUpperCase() + city.slice(1)}</span>
                      <div className={`multiplier-badge ${multiplier === 1 ? 'base' : multiplier > 1 ? 'higher' : 'lower'}`}>
                        {multiplier === 1 ? 'Base' : multiplier > 1 ? `+${Math.round((multiplier - 1) * 100)}%` : `${Math.round((multiplier - 1) * 100)}%`}
                      </div>
                    </div>
                    <div className="city-details">
                      <span>Compared to Lahore</span>
                      {multiplier !== 1 && (
                        <small>
                          {multiplier > 1 ? 'Higher cost' : 'Lower cost'} due to {city === 'islamabad' ? 'premium location' : city === 'karachi' ? 'logistics & labor' : 'local market factors'}
                        </small>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Popular Calculations */}
            <div className="popular-calculations">
              <div className="section-headers">
                <h3><FaChartBar className="section-icon" /> Popular Construction Calculations</h3>
                <p>Quick estimates for common property sizes</p>
              </div>
              <div className="popular-grid">
                {popularCalculations.map((item, index) => (
                  <div key={index} className="popular-item">
                    <div className="popular-header">
                      <h5>{item.size} {item.unit.charAt(0).toUpperCase() + item.unit.slice(1)}</h5>
                      <div className="popular-badge">Popular</div>
                    </div>
                    <p>{item.type}</p>
                    <span> {item.area}</span>
                    <button 
                      className="details-btn"
                      onClick={() => handlePopularSelect(item)}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="faq-section">
              <div className="section-headers">
                <h3>Frequently Asked Questions <FaQuestionCircle className="section-icon" size={30}/></h3>
                <p>Get answers to common construction cost questions</p>
              </div>
              
              <div className="faq-container">
                {faqData.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <div 
                      className="faq-question"
                      onClick={() => toggleFaq(index)}
                    >
                      <h4>{faq.question}</h4>
                      <motion.span 
                        className={`faq-arrow ${openFaqIndex === index ? 'open' : ''}`}
                        animate={{ rotate: openFaqIndex === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        ▼
                      </motion.span>
                    </div>
                    <AnimatePresence>
                      {openFaqIndex === index && (
                        <motion.div 
                          className="faq-answer"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <p>{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Popular Calculation Popup */}
      <AnimatePresence>
        {selectedPopular && (
          <motion.div 
            className="popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePopup}
          >
            <motion.div 
              className="popup-contents"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-headers">
                <h3>{selectedPopular.size} {selectedPopular.unit.charAt(0).toUpperCase() + selectedPopular.unit.slice(1)} Construction Details</h3>
                <button className="close-btn" onClick={closePopup}>✕</button>
              </div>
              <div className="popup-body">
                <div className="popup-info">
                  <div className="info-item">
                    <span>Property Type:</span>
                    <strong>{selectedPopular.type}</strong>
                  </div>
                  <div className="info-item">
                    <span>Total Area:</span>
                    <strong>{selectedPopular.area}</strong>
                  </div>
                  <div className="info-item">
                    <span>Construction Type:</span>
                    <strong>Double Story Complete House</strong>
                  </div>
                </div>
                <div className="popup-actions">
                  <button className="btn-primary" onClick={closePopup}>
                    Use This Size
                  </button>
                  <button className="btn-secondary" onClick={closePopup}>
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ConstructionCost;